import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PaymentSection from '../PaymentSection/PaymentSection';
import ColorThemes from '../ColorThemes/ColorThemes';
import OverviewTab, { useOverview } from '../product/OverviewTab';
import GuidesTab from '../product/GuidesTab';
import { getPaymentMode, isWatchfaceProduct } from '../../utils/productType';
import { getCustomTabsForProduct } from '../customTabs';
import ScrollSpyTabs from '../ScrollSpyTabs/ScrollSpyTabs';
import { BookIcon, KeyIcon, ListIcon, OverviewIcon, ThemesIcon, TouchIcon } from '../ui/GuideIcons';
import SectionNav from '../product/SectionNav';
import './DetailTabs.css';
/** Dezente Hintergrund-Bänder je Abschnitt (Tokens in styles/dark-mode.css) */
const BANDS = { guides: 'b', buy: 'buy', themes: 'c' };
/** Themenfarbe je Abschnitt (Icon, aktiver Tab) */
const SECTION_TONES = {
    overview: 'accent',
    'newgen-controls': 'key',
    guides: 'chart',
    buy: 'key',
    themes: 'display',
};
const toneOf = (id, custom) => SECTION_TONES[id] || (custom ? 'air' : 'accent');
const SECTION_ICONS = {
    overview: OverviewIcon,
    guides: ListIcon,
    buy: KeyIcon,
    themes: ThemesIcon,
    'newgen-controls': TouchIcon,
};
/**
 * Reihenfolge: Kaufen, Überblick, Anleitungen, dann produktspezifische Tabs und Themes.
 * Alles steht untereinander; die Leiste springt zum Abschnitt und markiert den sichtbaren.
 */
const DetailTabs = ({ product, colorThemes }) => {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').split('-')[0];
    const customTabDefs = useMemo(() => getCustomTabsForProduct(product), [product]);
    const overview = useOverview(product);
    const mode = getPaymentMode(product.is_vx);
    const isWatchface = isWatchfaceProduct(product.is_vx);
    const hasOverview = !!(overview?.description || overview?.features?.length || product.compatibleDeviceIds?.length);
    const tabs = useMemo(() => {
        const list = [];
        if (hasOverview)
            list.push({ id: 'overview', label: t('pages.product.tabs.overview'), order: 0 });
        customTabDefs.forEach((def) => list.push({
            id: def.id,
            label: def.labelKey ? t(def.labelKey) : def.label?.[lang] || def.label?.en || def.id,
            order: 10 + def.order,
            customDef: def,
        }));
        list.push({ id: 'guides', label: t('pages.product.tabs.guides'), order: 2 });
        if (mode !== 4)
            list.push({ id: 'buy', label: t('pages.product.tabs.buy'), order: -1 });
        if (isWatchface)
            list.push({ id: 'themes', label: t('pages.product.tabs.themes'), order: 70 });
        return list.sort((a, b) => a.order - b.order);
    }, [t, lang, customTabDefs, hasOverview, mode, isWatchface]);
    const sections = useMemo(() => tabs.map(({ id, label, customDef }) => {
        const Icon = SECTION_ICONS[id] || BookIcon;
        const tone = toneOf(id, !!customDef);
        return { id, label, tone, icon: _jsx(Icon, { size: 22, tone: tone }) };
    }), [tabs]);
    const sectionIcon = (id, custom) => {
        const Icon = SECTION_ICONS[id] || BookIcon;
        return (_jsx("span", { className: "icon-tile frame section-title-icon", children: _jsx(Icon, { size: 30, tone: toneOf(id, custom) }) }));
    };
    const renderContent = (tab) => {
        if (tab.customDef) {
            const Component = tab.customDef.component;
            // Apps haben ausführliche Anleitungen: Untermenü mit Icons zum Springen
            return isWatchface ? (_jsx(Component, { product: product })) : (_jsx(SectionNav, { children: _jsx(Component, { product: product }) }));
        }
        switch (tab.id) {
            case 'overview':
                return _jsx(OverviewTab, { product: product });
            case 'guides':
                return _jsx(GuidesTab, { product: product });
            case 'buy':
                return _jsx(PaymentSection, { product: product });
            case 'themes':
                return _jsx(ColorThemes, { themes: colorThemes });
            default:
                return null;
        }
    };
    return (_jsx(ScrollSpyTabs, { sections: sections, variant: "prominent", children: (registerRef) => tabs.map((tab) => (_jsxs("section", { id: `section-${tab.id}`, ref: registerRef(tab.id), className: `content-section product-section band-${tab.customDef ? 'a' : BANDS[tab.id] || 'none'}`, "aria-labelledby": `section-title-${tab.id}`, children: [_jsxs("h2", { id: `section-title-${tab.id}`, className: "product-section-title", children: [sectionIcon(tab.id, !!tab.customDef), _jsx("span", { children: tab.label })] }), renderContent(tab)] }, tab.id))) }));
};
export default DetailTabs;
